import React from 'react';
import { Shield, Building2, ExternalLink, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { Button } from '../components/ui/button';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/xat7ll2r_FOR%20ACQ.png';

const MerchantLoginACQ = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';

  return (
    <div className="min-h-screen flex flex-col" data-testid="acq-login-page">
      <Header />
      <div className="h-20" />

      <section
        className="relative flex-1 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        data-testid="acq-hero"
      >
        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-14">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-black/80 hover:text-black font-semibold mb-8 transition-colors duration-200"
            data-testid="acq-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="mb-10 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/50 text-[#c24414] text-[11px] font-bold uppercase tracking-[0.15em] mb-5 backdrop-blur-sm shadow-sm">
              <Building2 size={13} />
              ACQ
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-5 leading-tight" data-testid="acq-title">
              ACQ (Acquiring Platform)
            </h1>
            <p className="text-lg text-black leading-relaxed max-w-3xl font-medium" data-testid="acq-subtitle">
              eCOMM's next-generation payment platform, built to support merchants across eCommerce and in-store environments. Designed with scalability, flexibility and efficiency in mind, ACQ enables seamless transaction processing, settlement management and real-time reporting through an intuitive interface.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
            {/* Sign-in card */}
            <div className="md:col-span-2 rounded-lg bg-white/30 backdrop-blur-md border border-white/40 p-7 shadow-sm" data-testid="acq-cta-card">
              <h2 className="text-xl font-bold text-black mb-3">Sign-in to ACQ Portal</h2>
              <p className="text-sm text-black/80 leading-relaxed mb-2">
                Seamless payment processing across eCommerce and in-store channel to provide a streamlined experience for managing transactions, settlements and reporting in one place.
              </p>
              <p className="text-sm text-black/80 leading-relaxed mb-6">
                Log in here to access your merchant account, view transactions and manage your payment activity.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-[#1F4E79] hover:bg-[#163a5f] text-white text-base px-7 py-5"
                data-testid="acq-open-portal-btn"
              >
                <a href="https://mp.ecomm365.com/Portal/#/passport/login" target="_blank" rel="noopener noreferrer">
                  Open Merchant  Portal
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>

              <div className="mt-7 pt-5 border-t border-black/15">
                <h3 className="text-xs font-bold text-black uppercase tracking-[0.15em] mb-3">Need help?</h3>
                <ul className="space-y-2 text-sm text-black/85">
                  <li>• Forgotten password? Use the portal's "Forgot password" link</li>
                  <li>• Account locked? Contact <a href="mailto:csr@ecomm365.com" className="text-[#1F4E79] font-semibold hover:underline">csr@ecomm365.com</a></li>
                </ul>
              </div>
            </div>

            {/* Secure-access card */}
            <div className="rounded-lg bg-white/30 backdrop-blur-md border border-white/40 p-6 h-fit shadow-sm" data-testid="acq-security-card">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={18} className="text-emerald-700" />
                <h3 className="font-bold text-black">Secure Access</h3>
              </div>
              <p className="text-sm text-black/85 leading-relaxed">
                All ACQ portal sessions use TLS 1.2+ with PCI-DSS compliant hosting. Credentials are never logged or shared with third parties — eCOMM staff will never ask for your password.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default MerchantLoginACQ;
